import React, { useEffect, useState } from 'react';
import { FileText, Download, Eye, Search, Calendar } from 'lucide-react';
import SEO from './SEO';
import config from '../utils/config';

const API_URL = config.url;

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename || 'press.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (e) {
    console.error('Download error:', e);
    alert('Download failed. Please try again.');
  }
};

const PressPage = () => {
  const [pressList, setPressList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPress();
  }, []);

  const fetchPress = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.url}/press`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setPressList(data);
    } catch (err) {
      setError('Failed to load press releases');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = pressList.filter(
    (item) =>
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      (item.description || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <SEO 
        title="प्रेस रिलीज़"
        description="हमारे नवीनतम प्रेस रिलीज़ और मीडिया कवरेज को देखें और डाउनलोड करें। सीधी सोच सीधी बात के सभी महत्वपूर्ण घोषणाओं और समाचारों को यहाँ पढ़ें।"
        keywords="प्रेस रिलीज़, मीडिया कवरेज, सीधी सोच सीधी बात, समाचार, घोषणाएं, जनसेवा, सामाजिक कार्य"
        url="/press"
      />
      <section className="min-h-screen py-12 px-4 bg-gradient-to-br from-orange-50 via-yellow-50 to-pink-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent leading-tight">
            प्रेस रिलीज़
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            हमारे नवीनतम प्रेस रिलीज़ और मीडिया कवरेज को देखें और डाउनलोड करें
          </p>
        </div>

        {/* Search Bar */}
        <div className="flex justify-center mb-10">
          <div className="relative w-full max-w-md">
            <input
              type="text"
              placeholder="प्रेस रिलीज़ खोजें..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border-2 border-orange-200 rounded-2xl px-6 py-4 pl-12 focus:outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-400 bg-white shadow-lg transition-all duration-300 text-lg"
            />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-orange-400" />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-10">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-md mx-auto">
              <p className="text-red-600 font-medium">{error}</p>
              <button 
                onClick={fetchPress}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Press List */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.length === 0 ? (
              <div className="col-span-full text-center py-20">
                <div className="bg-white rounded-3xl p-12 shadow-xl max-w-md mx-auto">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    कोई प्रेस रिलीज़ नहीं मिला
                  </h3>
                  <p className="text-gray-500">
                    {search ? 'आपकी खोज के अनुसार कोई परिणाम नहीं मिला।' : 'अभी तक कोई प्रेस रिलीज़ नहीं जोड़ा गया है।'}
                  </p>
                </div>
              </div>
            ) : (
              filtered.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 border border-orange-100 group"
                >
                  {/* PDF Icon Placeholder */}
                  <div className="flex justify-center mb-6">
                    <div className="w-24 h-32 rounded-2xl bg-gradient-to-br from-orange-100 to-pink-100 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                      <FileText className="w-12 h-12 text-orange-500" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-gray-800 line-clamp-2 group-hover:text-orange-600 transition-colors">
                      {item.title}
                    </h3>
                    
                    {item.description && (
                      <p className="text-gray-600 line-clamp-3 leading-relaxed">
                        {item.description}
                      </p>
                    )}

                    {/* Date */}
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-2" />
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString('hi-IN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'Date not available'}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                      <a
                        href={getFullUrl(item.pdf_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-700 hover:bg-blue-100 py-3 px-4 rounded-xl font-medium transition-all duration-200 hover:scale-105"
                        title="View PDF"
                      >
                        <Eye className="w-5 h-5" />
                        देखें
                      </a>
                      <button
                        onClick={() =>
                          downloadFile(
                            getFullUrl(item.pdf_url),
                            item.title
                              ? item.title.replace(/[^a-zA-Z0-9-_]/g, '_') + '.pdf'
                              : 'press.pdf'
                          )
                        }
                        className="flex-1 flex items-center justify-center gap-2 bg-orange-50 text-orange-700 hover:bg-orange-100 py-3 px-4 rounded-xl font-medium transition-all duration-200 hover:scale-105"
                        title="Download PDF"
                      >
                        <Download className="w-5 h-5" />
                        डाउनलोड
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Results Count */}
        {!loading && !error && filtered.length > 0 && (
          <div className="text-center mt-12 text-gray-600">
            <p>
              {filtered.length} of {pressList.length} press release{filtered.length !== 1 ? 's' : ''} found
              {search && ` for "${search}"`}
            </p>
          </div>
        )}
      </div>
    </section>
    </>
  );
};

export default PressPage; 